import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import {
  formatPriceCompact,
  formatTypology,
  assetUrl,
} from "../lib/format";

/**
 * Marketplace design card — boxed (not floating).
 *
 * Frame: 1 px line border, no drop shadow, calm cream fill. Hover shifts the
 * border to terracotta and zooms the image inside the frame by 1.04×.
 *
 * Hierarchy (top to bottom):
 *   1. Image (visual hero)               · optional 10-Day Path pill overlay
 *   2. Eyebrow      — DUPLEX · 40 FT STANDARD    (tiny terracotta caps)
 *   3. Title        — Beville Twin               (largest text on card)
 *   4. Dek          — Near Eastside              (italic Newsreader)
 *   5. Hairline rule
 *   6. Price        — $342k all-in               (large tabular terracotta)
 *   7. Specs        — 3 Beds · 2 Baths · 1,541 Square Feet (small tabular ink-soft)
 *
 * All transitions respect prefers-reduced-motion.
 */
export default function DesignCard({ design, contextLotLabel }) {
  const navigate = useNavigate();
  const photo = assetUrl(design.exterior_photo);
  const lotLabel =
    contextLotLabel ||
    (design.min_lot_width >= 50
      ? "50 ft +"
      : design.min_lot_width >= 40
      ? "40 ft Standard"
      : "30 ft Narrow");

  return (
    <Link
      to={`/marketplace/${design.id}`}
      className="wn-design-card group block"
      style={{
        textDecoration: "none",
        color: "inherit",
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        overflow: "hidden",
      }}
      data-testid={`design-card-${design.id}`}
    >
      {/* 1 · Image */}
      <div
        className="wn-design-card-image"
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 3",
          overflow: "hidden",
          backgroundColor: "#EEEDF3",
        }}
      >
        <div
          aria-hidden
          className="wn-design-card-image-inner"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: photo ? `url(${photo})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {design.ten_day_eligible && (
          <span
            data-testid="ten-day-badge"
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 11px 6px 10px",
              borderRadius: 999,
              backgroundColor: "rgba(27, 31, 75, 0.92)",
              color: "#FBFAFD",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              backdropFilter: "saturate(140%)",
            }}
          >
            <Zap size={11} strokeWidth={2.5} />
            10-Day Path
          </span>
        )}
        {/* "N built" momentum pill — single-glance proof that this
            plan has been carried through construction before. Surfaces
            the same prior_builds data that drives the design detail's
            "Previously built" block, so the safety story is consistent
            from the architect profile down to the design page. Hidden
            for plans that have not been built yet (In-Review / Draft). */}
        {(design.prior_builds || []).length > 0 && (
          <span
            data-testid={`design-card-built-count-${design.id}`}
            title={`${design.prior_builds.length} ${
              design.prior_builds.length === 1 ? "completed home" : "completed homes"
            } built from this plan`}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              display: "inline-flex",
              alignItems: "baseline",
              gap: 5,
              padding: "5px 10px",
              borderRadius: 3,
              backgroundColor: "rgba(232, 234, 217, 0.96)",
              color: "#3A4A35",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: "1px solid rgba(184, 191, 160, 0.9)",
              lineHeight: 1.2,
            }}
          >
            <span
              className="tabular-nums"
              style={{ fontSize: 12.5, letterSpacing: "0em" }}
            >
              {design.prior_builds.length}
            </span>
            built
          </span>
        )}
      </div>

      <div style={{ padding: "18px 20px 20px" }}>
        {/* 2 · Eyebrow — kicker */}
        <div
          className="wn-label"
          style={{ color: "#B14E2F" }}
          data-testid="design-card-eyebrow"
        >
          {formatTypology(design.typology)}
          <span aria-hidden style={{ color: "#9AA0C0", margin: "0 8px" }}>
            ·
          </span>
          {lotLabel}
        </div>

        {/* 3 · Title — primary */}
        <h3
          className="wn-design-card-title mt-2"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 25,
            color: "#1B1F4B",
            lineHeight: 1.15,
            letterSpacing: "-0.005em",
            display: "inline",
          }}
        >
          {design.name}
        </h3>

        {/* 4 · Dek */}
        <div
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 15,
            lineHeight: 1.45,
          }}
        >
          {design.primary_neighborhood || "Indianapolis"}
        </div>

        {/* 4b · Architect byline — low prominence, italic, hairline-grey */}
        {(design.architect_firm || design.architect_name) && (
          <div
            className="mt-1"
            data-testid="design-card-architect"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#9AA0C0",
              fontSize: 12.5,
              lineHeight: 1.4,
            }}
          >
            by{" "}
            {design.architect_id ? (
              <span
                role="link"
                tabIndex={0}
                data-testid="design-card-architect-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/architects/${design.architect_id}`);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/architects/${design.architect_id}`);
                  }
                }}
                style={{
                  color: "#6C6B85",
                  cursor: "pointer",
                  borderBottom: "1px dotted #C9C8D4",
                  paddingBottom: 1,
                  transition: "color 160ms ease, border-color 160ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#8F3C23";
                  e.currentTarget.style.borderBottomColor = "#B14E2F";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6C6B85";
                  e.currentTarget.style.borderBottomColor = "#C9C8D4";
                }}
              >
                {design.architect_firm || design.architect_name}
              </span>
            ) : (
              <span style={{ color: "#6C6B85" }}>
                {design.architect_firm || design.architect_name}
              </span>
            )}
          </div>
        )}

        {/* 5 · Hairline */}
        <div
          style={{
            borderTop: "1px solid #D9D8E4",
            marginTop: 16,
            paddingTop: 14,
          }}
        >
          {/* 6 · Price — secondary primary */}
          <div className="flex items-baseline" style={{ columnGap: 8 }}>
            <span
              className="tabular-nums"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 24,
                color: "#8F3C23",
                lineHeight: 1,
                letterSpacing: "-0.005em",
              }}
              data-testid="design-card-price"
            >
              {formatPriceCompact(design.all_in_cost)}
            </span>
            <span className="wn-label" style={{ color: "#6C6B85" }}>
              estimated all-in
            </span>
          </div>

          {/* 6b · Variance-adjusted price — surfaces the median-of-actuals
              figure to the comparison shopper before they click through.
              Only renders for designs with ≥2 prior builds (a single
              build is not a median; the "N BUILT" pill on the image
              already carries that story). Uses the same median-variance
              math as the design-detail FactSheet — single source of
              truth. */}
          {(() => {
            const pb = design.prior_builds || [];
            if (pb.length < 2) return null;
            const variances = pb
              .map((b) => Number(b.variance_vs_estimate_pct))
              .filter((n) => !Number.isNaN(n))
              .sort((a, b) => a - b);
            if (variances.length < 2) return null;
            const mid = Math.floor(variances.length / 2);
            const medianVariance =
              variances.length % 2 === 0
                ? (variances[mid - 1] + variances[mid]) / 2
                : variances[mid];
            const adjusted = Math.round(
              (design.all_in_cost || 0) * (1 + medianVariance / 100)
            );
            return (
              <div
                className="mt-1.5 flex items-baseline"
                data-testid={`design-card-actuals-${design.id}`}
                style={{ columnGap: 8 }}
              >
                <span
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 17,
                    color: "#3A4A35",
                    lineHeight: 1.1,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {formatPriceCompact(adjusted)}
                </span>
                <span
                  className="wn-label"
                  style={{ color: "#4F6149", letterSpacing: "0.06em" }}
                >
                  median of {pb.length} actuals
                </span>
              </div>
            );
          })()}

          {/* 7 · Specs — tertiary, written out (no acronyms) */}
          <div
            className="tabular-nums mt-2 flex items-center flex-wrap"
            style={{
              color: "#6C6B85",
              fontSize: 13,
              columnGap: 8,
              rowGap: 2,
            }}
            data-testid="design-card-specs"
          >
            <span>{design.beds} {design.beds === 1 ? "Bed" : "Beds"}</span>
            <span aria-hidden style={{ color: "#9AA0C0" }}>·</span>
            <span>{design.baths} {design.baths === 1 ? "Bath" : "Baths"}</span>
            <span aria-hidden style={{ color: "#9AA0C0" }}>·</span>
            <span>{design.sqft?.toLocaleString() ?? "—"} Square Feet</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
